<script lang="ts" setup>
  import type { WideHeroStoryblok, HeroWideItemStoryblok, MultilinkStoryblok } from '@/storyblok/component-types-sb'
  import useFSFDataLayer from '@/composables/data-layers/useFSFDataLayer'
  import { cva } from 'class-variance-authority'
  import { useBTBreakpoints } from '@/composables/useBTBreakpoints'
  import { FLT } from '@/utils/constants/rip'
  import { useRecentSearch } from '@/composables/fsf/useRecentSearch'

  const props = defineProps<{
    blok: WideHeroStoryblok
  }>()

  const heroBody = computed(() => props.blok?.body)

  const isValidBody = computed(() => {
    const body = heroBody.value
    return body && Array.isArray(body) && body.length > 0
  })

  const selectedSectionIndex = ref(0)

  interface IWideHeroSection {
    value: string
    key: string | undefined
    index: number
    uid: string | number
    href: MultilinkStoryblok
  }

  const { pushFsfTopLabelsDataLayer } = useFSFDataLayer()

  const onSectionSelect = async (section: IWideHeroSection) => {
    const sectionUrl = section.href?.url ?? section.href?.cached_url
    if (sectionUrl) {
      await navigateTo(sectionUrl, { external: sectionUrl.startsWith('http') })

      return
    }

    if (isValidBody.value && section.index >= 0 && section.index < heroBody.value.length) {
      selectedSectionIndex.value = section.index

      pushFsfTopLabelsDataLayer({
        event_category: section.key,
        event_action: 'click'
      })
    }
  }

  const sections = computed<IWideHeroSection[]>(
    () =>
      heroBody.value?.map((item: HeroWideItemStoryblok, index: number) => ({
        value: item.buttonText || '',
        key: item.key,
        index,
        uid: item._uid || index,
        href: item.href || { url: '', cached_url: '' }
      })) ?? []
  )

  watch(
    () => heroBody.value?.length,
    (length) => {
      if (length && selectedSectionIndex.value >= length) {
        selectedSectionIndex.value = 0
      }
    },
    { immediate: true }
  )

  const heroItem = computed<HeroWideItemStoryblok | null>(() => {
    if (!isValidBody.value) {
      return null
    }
    const index = selectedSectionIndex.value
    if (index < 0 || index >= heroBody.value.length) {
      return heroBody.value[0]
    }

    return heroBody.value[index]
  })

  const heroContent = computed(() => {
    if (!heroItem.value) {
      return null
    }

    const items = heroItem.value.item || []
    const [firstItem] = items

    return firstItem ?? null
  })

  const showOverlayLoader = ref(false)

  const submitAction = () => {
    showOverlayLoader.value = true
  }

  const sectionElement = ref<HTMLElement | null>(null)
  onElementVisibleAtThreshold(sectionElement, () =>
    pushFsfTopLabelsDataLayer({
      event_action: 'impressions'
    })
  )

  const focusedSectionIndex = ref(0)
  const tabRefs = ref<Array<HTMLElement | null>>([])

  const setTabRef = (el: HTMLElement | null, i: number) => {
    tabRefs.value[i] = el
  }

  const focusTab = (i: number) => {
    const count = sections.value.length
    if (!count) return

    const next = ((i % count) + count) % count
    focusedSectionIndex.value = next
    tabRefs.value[next]?.focus()
  }

  const activateTab = async (i: number) => {
    const section = sections.value[i]
    if (section) {
      await onSectionSelect(section)
    }
  }

  const onTablistKeydown = async (e: KeyboardEvent) => {
    const count = sections.value.length
    if (count <= 1) return

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault()
        focusTab(focusedSectionIndex.value + 1)
        break

      case 'ArrowLeft':
        e.preventDefault()
        focusTab(focusedSectionIndex.value - 1)
        break

      case 'Home':
        e.preventDefault()
        focusTab(0)
        break

      case 'End':
        e.preventDefault()
        focusTab(sections.value.length - 1)
        break

      case 'Enter':
      case ' ':
        e.preventDefault()
        await activateTab(focusedSectionIndex.value)
        break
    }
  }

  watch(
    () => sections.value.length,
    (len) => {
      if (!len) return
      if (selectedSectionIndex.value >= len) selectedSectionIndex.value = 0
      if (focusedSectionIndex.value >= len) focusedSectionIndex.value = selectedSectionIndex.value
    },
    { immediate: true }
  )

  watch(
    () => selectedSectionIndex.value,
    (i) => {
      focusedSectionIndex.value = i
    }
  )

  const { isDesktop } = useBTBreakpoints()

  const { recentSearch } = useRecentSearch()
  const hasRecentSearchItems = computed(() => !!recentSearch.value?.items?.length)

  const { pageType } = usePageType()

  const isSectionVariant = (key: unknown): key is SectionVariant =>
    typeof key === 'string' && SECTION_VARIANTS.includes(key as SectionVariant)

  const selectedSectionKey = computed(() => sections.value[selectedSectionIndex.value]?.key)
  const selectedSectionVariant = computed<SectionVariant>(() => {
    const key = selectedSectionKey.value
    return isSectionVariant(key) ? key : 'book_flight'
  })

  const SECTION_HEIGHTS = {
    book_flight: 'md:max-h-128 lg:min-h-128 xl:min-h-128',
    flight_and_hotel: 'md:min-h-190 lg:min-h-135 lg:max-h-135 xl:min-h-132 xl:max-h-132',
    hotel: 'md:max-h-128 lg:min-h-128 lg:max-h-128 xl:min-h-128 xl:max-h-128',
    cars: 'md:max-h-128 lg:min-h-128 lg:max-h-128 xl:min-h-128 xl:max-h-128'
  } as const

  type SectionVariant = keyof typeof SECTION_HEIGHTS
  const SECTION_VARIANTS = Object.keys(SECTION_HEIGHTS) as SectionVariant[]

  const heroItemVariants = cva('mt-4 md:mx-10 md:mt-10', {
    variants: { selectedSection: SECTION_HEIGHTS }
  })

  const heroItemClass = computed(() => {
    if (pageType.value !== FLT) return 'mt-4 md:mx-10 md:mt-10'

    if (selectedSectionVariant.value === 'book_flight' && hasRecentSearchItems.value) {
      return 'mt-4 md:mx-10 md:mt-10 md:min-h-145 lg:min-h-150 xl:min-h-140'
    }

    return heroItemVariants({ selectedSection: selectedSectionVariant.value })
  })
</script>

<template>
  <div
    v-if="heroItem"
    :id="heroItem._uid"
    ref="sectionElement"
    role="tabpanel"
    :aria-labelledby="`${heroItem._uid}-button`"
    :class="heroItemClass"
  >
    <HeroWide :item="heroItem">
      <template #default>
        <div class="flex flex-col gap-4 rounded-md bg-white pb-6 pt-4 md:pt-6">
          <div
            v-if="sections.length > 1"
            class="flex w-full gap-2 overflow-x-auto px-4 py-2 md:gap-4 md:px-8"
            :aria-label="blok.tablistAriaLabel"
            role="tablist"
            @keydown="onTablistKeydown"
          >
            <UiButton
              v-for="(section, i) in sections"
              :id="`${section.uid}-button`"
              :key="section.uid"
              :ref="(el) => setTabRef(el as HTMLElement | null, i)"
              :title="section.value"
              :variant="selectedSectionIndex === section.index ? 'secondary' : 'border'"
              size="sm"
              role="tab"
              :aria-selected="selectedSectionIndex === section.index"
              :tabindex="focusedSectionIndex === i ? 0 : -1"
              :aria-controls="section.uid"
              class="h-8 flex-shrink-0 !rounded-lg !py-0 transition-colors duration-300 text-subtitle-2 hover:border-bt-blue-dark md:h-10 md:min-w-32"
              @focus="focusedSectionIndex = i"
              @click="onSectionSelect(section)"
            />
            <NuxtImg
              v-if="blok.logo?.filename && isDesktop && selectedSectionKey === 'flight_and_hotel'"
              :src="blok.logo.filename"
              :alt="blok.logo.alt || undefined"
              :height="42"
              :width="212"
              class="ml-auto"
            />
          </div>
          <ClientOnly v-if="heroContent">
            <StoryblokComponent
              :key="heroContent._uid"
              v-editable="heroContent"
              :blok="heroContent"
              class="px-4 md:px-8"
              @submit="submitAction"
            />
            <template #fallback>
              <div class="flex h-full max-h-50 w-full items-center justify-center">
                <UiPlaneLoader class="max-h-50" />
              </div>
            </template>
          </ClientOnly>
        </div>
      </template>
    </HeroWide>
  </div>
  <FsfOverlay v-if="showOverlayLoader" />
</template>
